import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { useAvatar } from '../../contexts/AvatarContext';
import { useSignUpFeature } from '../../hooks/useFeatureFlags';
import { Avatar } from '../ui/avatar';
import ThemeToggle from '../ui/ThemeToggle';
import OrgadoroWideLogoForDark from '../../Orgadoro_WideLogo_ForDark.png';
import { Menu, X, User, LogOut, Settings } from 'lucide-react';

function AppNavbar() {
  const { user } = useAuth();
  const { avatar } = useAvatar();
  const { isSignUpEnabled } = useSignUpFeature();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.relative')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900 shadow-sm border-b border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                src={OrgadoroWideLogoForDark}
                alt="Orgadoro"
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {user ? (
                <>
                  <Link
                    to="/project-groups"
                    className="text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Project Groups
                  </Link>
                  <Link
                    to="/projects"
                    className="text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Projects
                  </Link>
                  <Link
                    to="/tasks"
                    className="text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Tasks
                  </Link>
                </>
              ) : null}
            </div>
          </div>

          {/* User Menu */}
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="flex items-center p-2 text-gray-100 hover:text-red-400 rounded-md transition-colors"
                  >
                    <Avatar
                      size="sm"
                      src={avatar?.url}
                      initials={avatar?.initials}
                      color={avatar?.color}
                      type={avatar?.type}
                    />
                  </button>
                  
                  {isMenuOpen && (
                    <div id="user-menu" className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </Link>
                      <Link
                        to="/preferences"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Preferences
                      </Link>
                      <hr className="my-1 border-gray-200 dark:border-gray-600" />
                      <div className="px-4 py-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                        </span>
                      </div>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <ThemeToggle />
                  <Link
                    to="/login"
                    className="text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Login
                  </Link>
                  {isSignUpEnabled && (
                    <Link
                      to="/sign-up"
                      className="bg-red-600 text-white hover:bg-red-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Sign Up
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-100 hover:text-red-400 p-2 rounded-md"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-gray-700">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {user ? (
                <>
                  <Link
                    to="/project-groups"
                    className="block text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Project Groups
                  </Link>
                  <Link
                    to="/projects"
                    className="block text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Projects
                  </Link>
                  <Link
                    to="/tasks"
                    className="block text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Tasks
                  </Link>
                  <hr className="my-2 border-gray-600" />
                  <Link
                    to="/profile"
                    className="block text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/preferences"
                    className="block text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Preferences
                  </Link>
                  <div className="px-3 py-2">
                    <span className="text-xs text-gray-400">
                      {user.email}
                    </span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <div className="px-3 py-2">
                    <ThemeToggle />
                  </div>
                  <Link
                    to="/login"
                    className="block text-gray-100 hover:text-red-400 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  {isSignUpEnabled && (
                    <Link
                      to="/sign-up"
                      className="block bg-red-600 text-white hover:bg-red-700 px-3 py-2 rounded-md text-base font-medium transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Sign Up
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default AppNavbar;